/**
 * `annotationBubbleCanvas.ts` — composes the annotation marker face into a 2D
 * canvas (used as a texture on the billboard plane in `annotationsRenderer`).
 *
 * Mirrors Unity's `Content/Controls/AnnotationVisual.prefab` — a compact white
 * rounded card with a soft drop shadow holding the comment text (small, gray,
 * wrapped). The author avatar (a colored initials circle) is drawn OVERLAPPING
 * the card's top-left corner — its centre sits on the corner point so half of
 * it floats outside the card, matching the inline comment editor. Selected
 * markers get the Campfire red highlight border (`#F84536`). No image/reply-pill
 * in this slice.
 *
 * Absolute pixel sizes here are a texture authoring resolution (the plane is
 * scaled to a constant screen size at draw time), so only the *ratios* matter.
 *
 * Deviation (user direction, 2026-10-28): the text is BLACK at 18px. The
 * prefab's gray (#5a5a5a here, at 22px) read as faded on the white card. The
 * inline editor in `ViewportView.vue` takes the same colour, so the text does
 * not change shade when the bubble opens for editing.
 */

export interface BubbleAuthor {
  readonly initials: string;
  readonly color: string;
}

const FONT_PX = 18;
const LINE_HEIGHT = Math.round(FONT_PX * 1.25);
const MAX_TEXT_WIDTH = 300;
const PAD_X = 16;
const PAD_Y = 10;
const AVATAR = 34;
const AVATAR_FONT_PX = 14;
const RADIUS = 22;
const SHADOW_MARGIN = 16;
/** Left/top canvas margin — must clear the avatar half that overflows the
 *  card's top-left corner, plus the shadow. */
const MARGIN_TL = Math.max(SHADOW_MARGIN, Math.ceil(AVATAR / 2) + 3);

const BG = "#ffffff";
const TEXT_COLOR = "#000000";
const SHADOW_COLOR = "rgba(0,0,0,0.42)";
const HIGHLIGHT = "#f84536";
/** Greycliff CF (`styles/fonts.css`), the brand face — Bold for the comment. */
const SANS = `"Greycliff CF", -apple-system, "Segoe UI", system-ui, sans-serif`;
const FONT = `700 ${FONT_PX}px ${SANS}`;
const AVATAR_FONT = `600 ${AVATAR_FONT_PX}px ${SANS}`;

/**
 * Resolves once the bubble's Greycliff faces are loaded. A canvas does not
 * wait for a web font: text drawn before it arrives is drawn — and measured —
 * in the fallback, and stays that way on the texture, so the renderer
 * recomposes its bubbles when this settles. Never rejects.
 */
export function loadAnnotationBubbleFonts(): Promise<void> {
  if (typeof document === "undefined" || !document.fonts) return Promise.resolve();
  return Promise.all([document.fonts.load(FONT), document.fonts.load(AVATAR_FONT)]).then(
    () => undefined,
    () => undefined,
  );
}

export interface ComposedBubble {
  readonly canvas: HTMLCanvasElement;
  readonly widthPx: number;
  readonly heightPx: number;
  /** Transparent px between the visible card's bottom edge and the texture's
   *  bottom edge — the connector line lifts its endpoint by this (× scale) so
   *  it touches the card body instead of ending in the shadow margin. */
  readonly bottomMarginPx: number;
}

/** Signature of everything the composed face depends on — lets the renderer
 *  skip recompositing when only the transform changed. */
export function bubbleContentKey(text: string, selected: boolean, initials: string): string {
  return `${selected ? "1" : "0"}|${initials}|${text}`;
}

export function composeAnnotationBubble(
  text: string,
  selected: boolean,
  author: BubbleAuthor,
): ComposedBubble {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;
  const display = text.trim().length > 0 ? text : "…";

  ctx.font = FONT;
  const lines = wrapText(ctx, display, MAX_TEXT_WIDTH);
  let textWidth = 0;
  for (const line of lines) textWidth = Math.max(textWidth, ctx.measureText(line).width);
  const textBlockHeight = lines.length * LINE_HEIGHT;

  // The avatar overlaps the corner (absolute), so the text block owns the whole
  // card interior — it is no longer inline with the avatar.
  const boxW = PAD_X + Math.ceil(textWidth) + PAD_X;
  const boxH = PAD_Y + textBlockHeight + PAD_Y;
  canvas.width = MARGIN_TL + boxW + SHADOW_MARGIN;
  canvas.height = MARGIN_TL + boxH + SHADOW_MARGIN;

  const bx = MARGIN_TL;
  const by = MARGIN_TL;

  // 1) White rounded card with a soft downward drop shadow.
  ctx.save();
  ctx.shadowColor = SHADOW_COLOR;
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 5;
  ctx.fillStyle = BG;
  roundRect(ctx, bx, by, boxW, boxH, RADIUS);
  ctx.fill();
  ctx.restore();

  // 2) Selection highlight border (Campfire red).
  if (selected) {
    ctx.strokeStyle = HIGHLIGHT;
    ctx.lineWidth = 3;
    roundRect(ctx, bx + 1.5, by + 1.5, boxW - 3, boxH - 3, RADIUS - 1.5);
    ctx.stroke();
  }

  // 3) Comment text (black, wrapped) — fills the card interior.
  ctx.font = FONT;
  ctx.fillStyle = TEXT_COLOR;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  const textX = bx + PAD_X;
  let ty = by + PAD_Y;
  for (const line of lines) {
    ctx.fillText(line, textX, ty);
    ty += LINE_HEIGHT;
  }

  // 4) Author avatar — colored initials circle CENTERED on the card's top-left
  //    corner, so half of it floats outside the card. Drawn last so it sits on
  //    top of the card edge.
  const ax = bx;
  const ay = by;
  ctx.beginPath();
  ctx.arc(ax, ay, AVATAR / 2, 0, Math.PI * 2);
  ctx.fillStyle = author.color;
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.font = AVATAR_FONT;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(author.initials || "?", ax, ay + 1);

  return {
    canvas,
    widthPx: canvas.width,
    heightPx: canvas.height,
    bottomMarginPx: SHADOW_MARGIN,
  };
}

/**
 * The texture size {@link composeAnnotationBubble} will produce for `text`,
 * without drawing it — so a placement can reserve the bubble's real footprint
 * before the bubble exists. With no DOM to measure in, every line is assumed
 * full width, which over-reserves rather than under.
 */
export function annotationBubbleSizePx(text: string): { widthPx: number; heightPx: number } {
  const display = text.trim().length > 0 ? text : "…";
  let lineCount: number;
  let textWidth: number;
  const ctx = typeof document === "undefined"
    ? null
    : document.createElement("canvas").getContext("2d");
  if (ctx) {
    ctx.font = FONT;
    const lines = wrapText(ctx, display, MAX_TEXT_WIDTH);
    lineCount = lines.length;
    textWidth = 0;
    for (const line of lines) textWidth = Math.max(textWidth, ctx.measureText(line).width);
  } else {
    lineCount = Math.max(1, Math.ceil((display.length * FONT_PX * 0.55) / MAX_TEXT_WIDTH));
    textWidth = MAX_TEXT_WIDTH;
  }
  const boxW = PAD_X + Math.ceil(textWidth) + PAD_X;
  const boxH = PAD_Y + lineCount * LINE_HEIGHT + PAD_Y;
  return {
    widthPx: MARGIN_TL + boxW + SHADOW_MARGIN,
    heightPx: MARGIN_TL + boxH + SHADOW_MARGIN,
  };
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const out: string[] = [];
  for (const paragraph of text.split("\n")) {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      out.push("");
      continue;
    }
    let line = words[0];
    for (let i = 1; i < words.length; i++) {
      const candidate = `${line} ${words[i]}`;
      if (ctx.measureText(candidate).width > maxWidth) {
        out.push(line);
        line = words[i];
      } else {
        line = candidate;
      }
    }
    out.push(line);
  }
  return out;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}
